import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DataService } from 'src/app/services/data.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-opt-out',
  templateUrl: './opt-out.component.html',
  styleUrls: ['./opt-out.component.scss'],
})
export class OptOutComponent implements OnInit {
  form: FormGroup;
  emailAddress = '';
  isSubmitting = false;
  private routeSub: Subscription;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService
  ) {
    this.form = this.fb.group({
      emailAddress: [{ value: '', disabled: true }],
      unsubscribeLogistics: [false],
      unsubscribeMarketing: [false],
    });
  }

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const emailFromPath = params.get('email') || '';
      const emailFromQuery = this.route.snapshot.queryParamMap.get('email') || '';
      const resolvedEmail = decodeURIComponent(emailFromPath || emailFromQuery).trim();

      this.emailAddress = resolvedEmail;
      this.form.patchValue({ emailAddress: this.emailAddress });

      if (this.emailAddress) {
        this.loadPreferences();
      } else {
        this.form.patchValue({
          unsubscribeLogistics: false,
          unsubscribeMarketing: false,
        });
      }
    });
  }

  loadPreferences(): void {
    this.dataService.getOptOutPreferences(this.emailAddress).subscribe(
      (res: any) => {
        if (res && res.exists) {
          this.form.patchValue({
            unsubscribeLogistics: !!res.unsubscribeLogistics,
            unsubscribeMarketing: !!res.unsubscribeMarketing,
          });
        }
      },
      () => {
        // proceed with default unchecked values when record does not exist or API returns error
      }
    );
  }

  unsubscribeAllEmails(): void {
    this.form.patchValue({
      unsubscribeLogistics: true,
      unsubscribeMarketing: true,
    });
  }

  onLogisticsChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.form.patchValue({ unsubscribeLogistics: !!target?.checked });
  }

  onMarketingChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.form.patchValue({ unsubscribeMarketing: !!target?.checked });
  }

  savePreferences(): void {
    if (this.isSubmitting) {
      return;
    }

    if (!this.emailAddress) {
      Swal.fire({
        icon: 'warning',
        title: 'Email Required',
        text: 'Email address is required to save your preference.',
      });
      return;
    }

    this.isSubmitting = true;

    const formValue = this.form.getRawValue();
    const payload = {
      emailAddress: this.emailAddress,
      unsubscribeLogistics: !!formValue.unsubscribeLogistics,
      unsubscribeMarketing: !!formValue.unsubscribeMarketing,
    };

    this.dataService.saveOptOutPreferences(payload).subscribe(
      (res: any) => {
        this.isSubmitting = false;

        if (res && res.success === false) {
          Swal.fire({
            icon: 'error',
            title: 'Submission Failed',
            text: res.message || 'Unable to save preference at this time.',
          });
          return;
        }

        Swal.fire({
          icon: 'success',
          title: 'Preference Submitted',
          text: 'Your user preference has been submitted.',
          confirmButtonText: 'OK',
        }).then(() => {
          this.router.navigateByUrl('/home');
        });
      },
      () => {
        this.isSubmitting = false;
        Swal.fire({
          icon: 'error',
          title: 'Submission Failed',
          text: 'Unable to save preference at this time.',
        });
      }
    );
  }

  cancel(): void {
    this.router.navigateByUrl('/home');
  }

  ngOnDestroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }
}
